import { api } from '../api/client';
import type { CustomEftDto, AniRecord } from '../workflows/eftBuilder';

/**
 * The Ani cache, from the browser's side.
 *
 * Each impression is sent the moment it is captured, so the transfer happens while the operator is
 * still working rather than all at once at the review step. A ten-print with slaps is several megabytes;
 * on a slow connection, uploading it in one request at review is a wait that lands precisely when
 * someone is standing there waiting to be told whether the EFT is valid.
 *
 * Nothing here blocks capture. Every upload is fire-and-forget with its own status, because a scanner
 * is a physical queue of people and must never stall on a network round trip.
 */

export interface ValidationIssue {
  code: number;
  name: string;
  description: string;
  message: string;
  location: { recordType?: number; recordIndex?: number; field?: number; subfield?: number; item?: number };
  fieldLabel?: string;
}

export interface BuildResult {
  valid: boolean;
  specification: string;
  issues: ValidationIssue[];
  /** Rules that could not run because the specification is at fault. Never the operator's problem. */
  ruleErrors: ValidationIssue[];
  /** Some rules did not run, so `valid` means "nothing found", not "checked completely". */
  incomplete: boolean;
  recordCount: number;
  size: number;
  missing: { recordType: number; fgp: number }[];
  unused: { recordType: number; fgp: number }[];
  retention: 'permanent' | '7-days';
}

export interface StagedImage {
  slot: string;
  fgp: number;
  width: number;
  height: number;
  size: number;
  /** True when the scanner's own WSQ was kept rather than re-encoded. */
  alreadyWsq: boolean;
  thumbnail: string;
}

export async function createCache(captureType: string, transactionId?: string): Promise<string> {
  const res = await api.post<{ cacheId: string }>('/eft/cache', {
    captureType,
    ...(transactionId ? { transactionId } : {}),
  });
  return res.cacheId;
}

/**
 * Stage one impression. `image` is base64 of whatever the scanner produced; the server converts it to
 * WSQ and returns a thumbnail. Re-uploading the same slot replaces it, which is what a recapture is.
 */
export function putImage(
  cacheId: string,
  input: { slot: string; fgp: number; recordType: number; impression?: number; image: string },
): Promise<StagedImage> {
  return api.put<StagedImage>(`/eft/cache/${encodeURIComponent(cacheId)}/image`, input);
}

/**
 * A full-resolution PNG of one staged impression, rendered on demand.
 *
 * Not part of the import payload: half a megabyte each, and most are never opened. Fetched when the
 * operator actually looks at a print, where the thumbnail's detail is not enough to judge it by.
 */
export function fullImage(cacheId: string, slot: string): Promise<{ png: string; width: number; height: number }> {
  return api.get(`/eft/cache/${encodeURIComponent(cacheId)}/image/${encodeURIComponent(slot)}`);
}

export function removeImage(cacheId: string, slot: string): Promise<void> {
  return api.del<void>(`/eft/cache/${encodeURIComponent(cacheId)}/image/${encodeURIComponent(slot)}`);
}

export function putDemographics(cacheId: string, demographics: Record<string, string>): Promise<void> {
  return api.put<void>(`/eft/cache/${encodeURIComponent(cacheId)}/demographics`, { demographics });
}

export function discardCache(cacheId: string): Promise<void> {
  return api.del<void>(`/eft/cache/${encodeURIComponent(cacheId)}`);
}

/**
 * Everything except the imagery.
 *
 * The whole point of the cache is that the pictures are already on the server, so sending them again to
 * be validated would undo it. `buildCustomEft` embeds each capture as `img`; this is the same document
 * with those stripped, which is a few kilobytes of text.
 */
export function skeletonOf(dto: CustomEftDto): { records: Omit<AniRecord, 'img'>[] } {
  const doc = dto.transaction as unknown as { records: AniRecord[]; idcPadded?: boolean };
  return {
    ...doc,
    records: doc.records.map(({ img: _img, ...rest }) => rest),
  };
}

/** Assemble the EFT on the server from the skeleton plus what has been staged, and verify it. */
export function buildAndValidate(
  cacheId: string,
  dto: CustomEftDto,
  transactionId?: string,
): Promise<BuildResult> {
  return api.post<BuildResult>(`/eft/cache/${encodeURIComponent(cacheId)}/build`, {
    transaction: skeletonOf(dto),
    ...(transactionId ? { transactionId } : {}),
  });
}

export interface ImportedEft {
  cacheId: string;
  tot: string | null;
  recordCount: number;
  /** Type-2 field number -> value. Mapped onto the form here, where the workflow config lives. */
  descriptive: Record<string, string>;
  images: (StagedImage & { recordType: number; wsq: string })[];
}

/**
 * Upload an EFT and read it into a cache.
 *
 * Two steps because the file is large: straight to the object store with a presigned PUT, then the
 * server reads it from there. Putting several megabytes through the API would be slower and is capped.
 */
export async function importEft(file: File, captureType: string): Promise<ImportedEft> {
  const { url, key } = await api.post<{ url: string; key: string }>('/eft/import-url', {});
  const put = await fetch(url, {
    method: 'PUT',
    headers: { 'content-type': 'application/octet-stream' },
    body: file,
  });
  if (!put.ok) throw new Error(`The file could not be uploaded (${put.status}).`);
  return api.post<ImportedEft>('/eft/import', { key, captureType });
}
